import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Button, Badge, Input, Select, Grid, StatCard, PageHeader, Modal, Alert } from '../../ui/components';
import { RefreshCw, HandMetal, Info } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const getScoreColor = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'danger';
};

const UnderwriterQueue = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    fetchQueue();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [applications, searchTerm, productFilter]);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      // Fetch unassigned applications ready for underwriting
      const { data, error } = await supabase
        .from('loan_applications')
        .select('*')
        .eq('status', 'submitted_for_underwriting')
        .is('underwriter_id', null)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Error fetching queue:', error);
      // Use empty array for demo
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = applications;

    if (productFilter !== 'all') {
      filtered = filtered.filter(app => app.product_type === productFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(app =>
        app.borrower_info?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.borrower_info?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.anonymous_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredApplications(filtered);
  };

  const handleClaimClick = (app) => {
    setSelectedApp(app);
    setClaimModalOpen(true);
  };

  const handleClaim = async () => {
    if (!selectedApp) return;

    setClaiming(true);
    try {
      // In production, update with actual underwriter_id
      const { error } = await supabase
        .from('loan_applications')
        .update({
          underwriter_id: 'current-underwriter-id', // Would come from auth
          status: 'underwriting_in_progress',
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedApp.id);

      if (error) throw error;

      // Navigate to application view
      navigate(`/uw/application/${selectedApp.id}`);
    } catch (error) {
      console.error('Error claiming application:', error);
      alert('Failed to claim application. Please try again.');
    } finally {
      setClaiming(false);
      setClaimModalOpen(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString()}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getTimeWaiting = (date) => {
    const now = new Date();
    const submitted = new Date(date);
    const diffHours = Math.floor((now - submitted) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
  };

  const products = [...new Set(applications.map(a => a.product_type).filter(Boolean))];

  const DetailRow = ({ label, children }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--lk-border-subtle)' }}>
      <span style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{label}</span>
      <span style={{ fontWeight: 500, fontSize: 'var(--lk-text-sm)' }}>{children}</span>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Unassigned Queue"
        description="Applications ready for underwriting"
        actions={<Button variant="primary" icon={RefreshCw} onClick={fetchQueue}>Refresh</Button>}
      />

      {/* Stats Cards */}
      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <StatCard label="Total Available" value={applications.length} />
        <StatCard label="High Priority" value={applications.filter(a => {
          const hours = (new Date() - new Date(a.created_at)) / (1000 * 60 * 60);
          return hours > 24;
        }).length} />
        <StatCard label="Avg Readiness" value={applications.length > 0
          ? Math.round(applications.reduce((sum, a) => sum + (a.readiness_score || 0), 0) / applications.length)
          : 0} />
        <StatCard label="Total Volume" value={formatCurrency(applications.reduce((sum, a) => sum + (a.loan_amount || 0), 0))} />
      </Grid>

      {/* Filters */}
      <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--lk-space-3)', alignItems: 'center' }}>
          <Input
            placeholder="Search by borrower name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}>
            <option value="all">All Products</option>
            {products.map(product => (
              <option key={product} value={product}>
                {product.replace(/_/g, ' ')}
              </option>
            ))}
          </Select>
          <div style={{ textAlign: 'right', color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>
            Showing {filteredApplications.length} of {applications.length}
          </div>
        </div>
      </Surface>

      {/* Applications Table */}
      <Surface padding="0" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="lk-table">
            <thead>
              <tr>
                <th>Application ID</th>
                <th>Borrower</th>
                <th>Product</th>
                <th style={{ textAlign: 'right' }}>Loan Amount</th>
                <th style={{ textAlign: 'center' }}>Readiness</th>
                <th>Submitted</th>
                <th>Waiting</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 'var(--lk-space-8)' }}>
                    <div className="lk-spinner lk-spinner-sm" style={{ display: 'inline-block', marginRight: 8 }} />
                    Loading queue...
                  </td>
                </tr>
              ) : filteredApplications.length > 0 ? (
                filteredApplications.map((app) => {
                  const waitingHours = (new Date() - new Date(app.created_at)) / (1000 * 60 * 60);
                  const isUrgent = waitingHours > 24;
                  
                  return (
                    <tr key={app.id} style={isUrgent ? { backgroundColor: 'var(--lk-danger-50)' } : {}}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {app.anonymous_id || `LK-${app.id.slice(0, 8).toUpperCase()}`}
                        {isUrgent && (
                          <Badge variant="danger" style={{ marginLeft: 8, fontSize: '0.6rem' }}>URGENT</Badge>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--lk-primary-100)', color: 'var(--lk-primary-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--lk-text-xs)', fontWeight: 600, flexShrink: 0 }}>
                            {app.borrower_info?.first_name?.[0] || '?'}
                          </div>
                          <div style={{ fontWeight: 500 }}>
                            {app.borrower_info?.first_name || 'Anonymous'} {app.borrower_info?.last_name || ''}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge variant="neutral">
                          {app.product_type?.replace(/_/g, ' ') || '-'}
                        </Badge>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(app.loan_amount)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <Badge variant={getScoreColor(app.readiness_score || 0)}>
                          {app.readiness_score || 0}
                        </Badge>
                      </td>
                      <td style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)' }}>{formatDate(app.created_at)}</td>
                      <td>
                        <Badge variant={isUrgent ? 'danger' : 'neutral'}>
                          {getTimeWaiting(app.created_at)}
                        </Badge>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Button
                          size="sm"
                          variant="primary"
                          icon={HandMetal}
                          onClick={() => handleClaimClick(app)}
                        >
                          Claim
                        </Button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--lk-text-quaternary)', padding: 'var(--lk-space-8)' }}>
                    {searchTerm || productFilter !== 'all'
                      ? 'No applications match your filters'
                      : 'Queue is empty - all applications are assigned!'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Surface>

      {/* Claim Confirmation Modal */}
      <Modal open={claimModalOpen} onClose={() => setClaimModalOpen(false)} title="Claim Application">
        {selectedApp && (
          <>
            <p style={{ marginBottom: 'var(--lk-space-3)' }}>Are you sure you want to claim this application?</p>
            <Surface padding="var(--lk-space-3)">
              <DetailRow label="Application ID">{selectedApp.anonymous_id || selectedApp.id.slice(0, 8)}</DetailRow>
              <DetailRow label="Borrower">{selectedApp.borrower_info?.first_name} {selectedApp.borrower_info?.last_name}</DetailRow>
              <DetailRow label="Product">{selectedApp.product_type?.replace(/_/g, ' ')}</DetailRow>
              <DetailRow label="Loan Amount">{formatCurrency(selectedApp.loan_amount)}</DetailRow>
              <DetailRow label="Readiness Score">
                <Badge variant={getScoreColor(selectedApp.readiness_score || 0)}>
                  {selectedApp.readiness_score || 0}
                </Badge>
              </DetailRow>
            </Surface>
            <Alert variant="info" icon={Info} style={{ marginTop: 'var(--lk-space-3)' }}>
              This application will be added to your pipeline and removed from the queue.
            </Alert>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--lk-space-2)', marginTop: 'var(--lk-space-4)' }}>
          <Button variant="secondary" onClick={() => setClaimModalOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleClaim} loading={claiming}>Claim Application</Button>
        </div>
      </Modal>
    </div>
  );
};

export default UnderwriterQueue;
